export const FRAMEWORK_IDS = [
  'dotnet',
  'dotnetfx',
  'angular',
  'react',
  'nodejs',
  'java',
  'spring-boot',
] as const;

export type FrameworkId = (typeof FRAMEWORK_IDS)[number];

export const isFrameworkId = (value: string): value is FrameworkId =>
  (FRAMEWORK_IDS as readonly string[]).includes(value);

/** endoflife.date product for each framework. */
export const FRAMEWORK_EOL_SLUGS: Record<FrameworkId, string> = {
  dotnet: 'dotnet',
  dotnetfx: 'dotnetfx',
  angular: 'angular',
  react: 'react',
  nodejs: 'nodejs',
  java: 'eclipse-temurin',
  'spring-boot': 'spring-boot',
};

/** One release cycle of a framework, as served by the framework-versions backend. */
export type EolRelease = { cycle: string; eol?: string; isEol: boolean };

export type DetectedFramework = { id: FrameworkId; version: string };

const NAMESPACE = 'idp.isolvedhcm.com/';

/** One label per detected version: idp.isolvedhcm.com/framework-<id>-<version>: 'true' */
export const FRAMEWORK_LABEL_PREFIX = `${NAMESPACE}framework-`;

/** Comma-separated folders, relative to the source location, to scan instead of its root. */
export const FRAMEWORK_PATHS_ANNOTATION = `${NAMESPACE}framework-paths`;

/** Short reason the last scan failed. Absent when the scan succeeded. */
export const FRAMEWORK_SCAN_ERROR_ANNOTATION = `${NAMESPACE}framework-scan-error`;

export const isFrameworkLabel = (key: string) =>
  key.startsWith(FRAMEWORK_LABEL_PREFIX);

// Label names are at most 63 characters and must end alphanumeric.
export const frameworkLabel = (id: FrameworkId, version: string) =>
  `${NAMESPACE}${`framework-${id}-${version}`
    .slice(0, 63)
    .replace(/[^A-Za-z0-9]+$/, '')}`;

export const compareVersions = (left: string, right: string): number => {
  const a = left.split('.').map(Number);
  const b = right.split('.').map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

export const readFrameworkLabels = (
  labels: Record<string, string> = {},
): DetectedFramework[] => {
  const keys = Object.keys(labels);
  return FRAMEWORK_IDS.flatMap(id => {
    const prefix = `${FRAMEWORK_LABEL_PREFIX}${id}-`;
    return keys
      .filter(key => key.startsWith(prefix))
      .map(key => key.slice(prefix.length))
      .sort(compareVersions)
      .map(version => ({ id, version }));
  });
};
